import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Box, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import jsPDF from "jspdf";
import "jspdf-autotable";
import MaterialReactTable from "material-react-table";
import React, { useEffect, useMemo, useState } from "react";
import { utils, writeFile } from "xlsx";
import fetchData from "../functions/fetchData";
import Select from "./Select";

export default function Table() {
  //data and fetching state

  const [initData, setInitData] = useState([]);
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  //table state

  useEffect(() => {
    (async () => {
      const json = await fetchData({
        data,
        setIsLoading,
        setIsRefetching,
        setIsError,
      });
      setInitData(json);
      setData(json);
    })();
  }, []);

  const handleExportPDF = () => {
    const pdf = new jsPDF();
    pdf.autoTable({ html: ".MuiTable-root" });
    pdf.save("data.pdf");
  };

  const buttonsData = [
    {
      label: "Export To PDF",
      icon: <PictureAsPdfIcon />,
      onClick: handleExportPDF,
    },
    {
      label: "Export To Excel",
      icon: <GridOnIcon />,
      onClick: handleExportExcel,
    },
  ];

  function handleExportExcel() {
    const wb = utils.table_to_book(document.querySelector(".MuiTable-root"));
    writeFile(wb, "SheetJSTable.xlsx");
  }

  const columns = useMemo(
    //column definitions...
    () => [
      {
        accessorKey: "date",
        header: "Date",
        Cell: ({ cell }) =>
          new Date(cell.getValue()).toLocaleDateString("en-GB"),
      },
      {
        accessorKey: "driver",
        header: "Driver",
        Cell: ({ cell }) => (
          <Box component="span" sx={{ textTransform: "capitalize" }}>
            {cell.getValue()}
          </Box>
        ),
      },
      {
        accessorKey: "coDriver",
        header: "CoDriver",
        Cell: ({ cell }) => (
          <Box component="span" sx={{ textTransform: "capitalize" }}>
            {cell.getValue()}
          </Box>
        ),
      },
      {
        accessorKey: "shippingDocs",
        header: "Shipping Docs",
      },
      {
        accessorKey: "dvir",
        header: "DVIR",
        Cell: ({ cell }) => (
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                position: "absolute",
                opacity: 0,
              }}
              component="span"
            >
              {cell.getValue() ? "Yes" : "No"}
            </Box>

            {cell.getValue() ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon />
            )}
          </Box>
        ),
      },
    ],
    []
    //end
  );

  const tableProps = {
    muiTableContainerProps: {
      sx: {
        maxHeight: "500px",
      },
    },
    state: {
      isLoading,

      showAlertBanner: isError,

      showProgressBars: isRefetching,
    },
    muiTopToolbarProps: {
      sx: {
        overflowX: "scroll",
        "&>*": {
          minWidth: "max-content",
        },
      },
    },

    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",

          children: "Error loading data",
        }
      : undefined,
    muiTablePaginationProps: {
      showFirstButton: true,
      showLastButton: true,
      labelRowsPerPage: "Items per page",
    },

    renderTopToolbarCustomActions: () => (
      <Stack direction="row" gap={1} mr={3}>
        {buttonsData.map((button) => (
          <Button
            key={button.label}
            startIcon={button.icon}
            variant="outlined"
            onClick={button.onClick}
          >
            {button.label}
          </Button>
        ))}
      </Stack>
    ),
    columns,
    data,
  };

  return (
    <Stack gap={1}>
      <TableTop
        data={data}
        setData={setData}
        initData={initData}
        setIsError={setIsError}
        setIsLoading={setIsLoading}
        setIsRefetching={setIsRefetching}
      />
      <MaterialReactTable {...tableProps} />
    </Stack>
  );
}

function TableTop({
  data,
  setData,
  initData,
  setIsError,
  setIsLoading,
  setIsRefetching,
}) {
  const [filters, setFilters] = useState({
    date_gte: "2016-05-01",
    date_lte: "2023-12-01",
    driver_eq: "All",
  });

  // function dateFilter(selectedDate, startingDate, endingDate) {
  //   return (
  //     new Date(selectedDate).getTime() >= new Date(startingDate).getTime() &&
  //     new Date(selectedDate).getTime() <= new Date(endingDate).getTime()
  //   );
  // }

  return (
    <Stack
      sx={{
        overflowX: "scroll",
        py: 2,
        "&>*": {
          minWidth: "max-content !important",
        },
      }}
      component="form"
      direction="row"
      alignItems="center"
      gap={4}
    >
      <TextField
        type="date"
        label="Starting date"
        value={filters.date_gte}
        inputProps={{ max: filters.date_lte }}
        onChange={(e) =>
          setFilters((prev) => ({ ...prev, date_gte: e.target.value }))
        }
      />
      <TextField
        type="date"
        label="Ending date"
        value={filters.date_lte}
        inputProps={{ min: filters.date_gte }}
        onChange={(e) =>
          setFilters((prev) => ({ ...prev, date_lte: e.target.value }))
        }
      />
      <Select
        initData={initData}
        setFilters={setFilters}
        driver={filters.driver_eq}
      />
      <Button
        variant="contained"
        onClick={async () => {
          const json = await fetchData({
            params: filters,
            data: initData,
            setIsLoading,
            setIsRefetching,
            setIsError,
          });

          setData(json);
        }}
      >
        Load records
      </Button>
    </Stack>
  );
}
